import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { cn, throttle } from "@/lib/utils";
import logo from "@/assets/baelleaves-logo.png";
import logoWebp from "@/assets/baelleaves-logo.webp";
import { InquiryForm } from "@/components/InquiryForm";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";

const navLinks = [
  { href: "/", key: "nav.home" },
  { href: "/circuits", key: "nav.circuits" },
  { href: "/gallery", key: "nav.gallery" },
  { href: "/stories", key: "nav.stories" },
  { href: "/baelleaves", key: "nav.baelleaves" },
  { href: "/tour-plans", key: "nav.tourPlans" },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const currentLang = i18n.resolvedLanguage || i18n.language || "en";

  useEffect(() => {
    const handleScroll = throttle(() => {
      setScrolled(window.scrollY > 50);
    }, 100);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  const isHome = location.pathname === "/";

  const LanguageSelector = () => (
    <div className="flex items-center gap-1.5 bg-secondary/70 hover:bg-secondary/100 border border-border/80 rounded-full px-3 py-1 transition-all duration-300">
      <Globe className="h-3.5 w-3.5 text-muted-foreground" />
      <select
        value={currentLang.split("-")[0]}
        onChange={(e) => changeLanguage(e.target.value)}
        className="bg-transparent text-xs font-semibold focus:outline-none cursor-pointer pr-1 text-foreground/80"
      >
        <option value="en" className="bg-background text-foreground">EN</option>
        <option value="ja" className="bg-background text-foreground">日本語</option>
      </select>
    </div>
  );

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-background border-b border-border shadow-soft"
    >
      <div className="container mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <picture>
              <source srcSet={logoWebp} type="image/webp" />
              <img loading="lazy" 
                src={logo} 
                alt="Bael Leaves" 
                // @ts-ignore
                fetchPriority="high"
                decoding="async"
                className="h-16 w-16 object-cover rounded-full"
              />
            </picture>
          </Link>

          {/* Mobile CTA - Centered */}
          <div className="md:hidden">
            <InquiryForm 
              trigger={
                <Button variant="sacred" size="sm">
                  {t("nav.requestTour")}
                </Button>
              }
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={cn(
                  "text-sm font-medium transition-all duration-300 relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-primary after:transition-all after:duration-300 hover:after:w-full",
                  location.pathname === link.href
                    ? "text-primary after:w-full"
                    : "text-foreground/70 hover:text-foreground"
                )}
              >
                {t(link.key)}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-4">
            <LanguageSelector />
            
            {/* CTA Button */}
            <InquiryForm 
              trigger={
                <Button variant="sacred" size="default">
                  {t("nav.requestTour")}
                </Button>
              }
            />
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-3 md:hidden">
            <LanguageSelector />
            <button
              className="p-2"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle menu"
            >
              {isOpen ? (
                <X className="h-6 w-6 transition-colors text-foreground" />
              ) : (
                <Menu className="h-6 w-6 transition-colors text-foreground" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden py-6 border-t border-border/50 animate-fade-in bg-background">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setIsOpen(false)}
                  className={cn(
                    "text-base font-medium py-2 transition-colors",
                    location.pathname === link.href
                      ? "text-primary"
                      : "text-foreground/70 hover:text-foreground"
                  )}
                >
                  {t(link.key)}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
